import React, { ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, LucideIcon } from 'lucide-react';

interface CivicModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: LucideIcon;
  children: ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | 'wide';
  headerAction?: ReactNode;
  footer?: ReactNode;
}

const maxWidthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-[560px]',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  wide: 'max-w-[720px]',
};

export default function CivicModal({
  isOpen,
  onClose,
  title,
  subtitle,
  icon: Icon,
  children,
  maxWidth = 'md',
  headerAction,
  footer,
}: CivicModalProps) {
  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEsc);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{
        '--bottom-nav-height': '72px',
        background: 'rgba(0, 0, 0, 0.55)',
        backdropFilter: 'blur(4px)',
        padding: '16px',
        paddingTop: 'max(16px, env(safe-area-inset-top, 16px))',
        paddingBottom: 'calc(var(--bottom-nav-height) + env(safe-area-inset-bottom, 0px) + 16px)',
      } as React.CSSProperties}
      onClick={onClose}
    >
      <div
        className={`relative rounded-2xl bg-[#141414] border border-[#282828] shadow-2xl flex flex-col overflow-hidden w-full ${maxWidthClasses[maxWidth]}`}
        style={{
          maxHeight: 'calc(100dvh - 32px - var(--bottom-nav-height) - env(safe-area-inset-bottom, 0px) - env(safe-area-inset-top, 0px))',
          boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-[#141414] flex-shrink-0 px-5 pt-6 relative rounded-t-2xl">
          <div className="absolute top-6 right-4 flex items-start gap-2 z-10">
            {headerAction}
            <button
              onClick={onClose}
              className="p-2 hover:bg-[#1A1A1A] rounded-lg transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-[#808080] hover:text-[#A0A0A0]" />
            </button>
          </div>

          <div className="flex items-center gap-3 pr-24">
            {Icon && (
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: 'rgba(64, 224, 208, 0.12)' }}
              >
                <Icon className="w-6 h-6" style={{ color: '#40E0D0' }} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3
                className="text-[20px] font-semibold text-[#F5F5F5] truncate md:line-clamp-2"
                style={{ letterSpacing: '-0.005em' }}
              >
                {title}
              </h3>
              {subtitle && (
                <p
                  className="text-[12px] text-[#A0A0A0] mt-0.5 line-clamp-2"
                  style={{ lineHeight: '1.4' }}
                >
                  {subtitle}
                </p>
              )}
            </div>
          </div>
        </div>

        <div
          className="px-5 pt-4 pb-6 overflow-y-auto flex-1"
          style={{
            minHeight: 0,
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {children}
        </div>

        {footer && (
          <div
            className="sticky bottom-0 z-10 flex-shrink-0 px-5 pt-4 pb-6"
            style={{
              background: '#141414',
              boxShadow: '0 -30px 30px 0 #141414',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
